<template>
  <div class="included-concepts-table">
    <AtlasAlert
      v-if="error"
      severity="danger"
      variant="tonal"
      class="included-concepts-table__error"
    >
      <div class="d-flex align-center">
        <span class="flex-grow-1">{{ error }}</span>
        <AtlasButton
          size="sm"
          variant="ghost"
          data-testid="included-retry-btn"
          @click="$emit('retry')"
        >
          {{ t('common.retry').value }}
        </AtlasButton>
      </div>
    </AtlasAlert>

    <ConceptFacetFilters
      v-if="items.length > 0"
      :facets="facets"
      :facet-options="facetOptions"
      :selected="selectedFacets"
      :active-filter-count="activeFilterCount"
      :result-filter="textFilter"
      text-field-test-id="included-result-filter"
      class="included-concepts-table__filters"
      @update:facet="({ key, values }) => setFacet(key, values)"
      @update:result-filter="setTextFilter"
      @clear="clearFilters()"
    >
      <template
        v-if="recordCountSources.length > 0"
        #append
      >
        <AtlasSelect
          :model-value="effectiveRecordCountSource"
          :items="recordCountSources"
          item-title="sourceName"
          item-value="sourceKey"
          :label="viewCountsForLabel"
          density="compact"
          variant="outlined"
          hide-details
          :loading="loadingRecordCounts"
          :menu-props="{ zIndex: 2300 }"
          data-testid="included-record-count-source"
          @update:model-value="(v: unknown) => setRecordCountSource(String(v))"
        />
      </template>
    </ConceptFacetFilters>

    <!-- These rows are the resolved expansion of the expression, not its items,
         so the way to drop one is to add an item excluding it (#224). -->
    <ConceptAddOptions
      v-if="!loading && items.length > 0"
      v-model="addFlags"
      :selected-count="selected.length"
      class="mb-3"
      @add="onAddSelected"
    />

    <AtlasCard
      v-if="loading || items.length > 0"
      padding="none"
    >
      <AtlasDataTable
        v-model:sort-by="sortBy"
        v-model:items-per-page="pageSize"
        :headers="headers"
        :items="visibleConcepts"
        :loading="loading"
        must-sort
        multi-sort
        hover
        class="included-concepts-table__table"
      >
        <template #header.select>
          <div data-testid="included-concepts-select-all">
            <v-checkbox-btn
              :model-value="allVisibleSelected"
              :indeterminate="someVisibleSelected && !allVisibleSelected"
              :aria-label="t('components.conceptTable.selectAllConcepts').value"
              density="compact"
              hide-details
              @update:model-value="onToggleSelectAll"
            />
          </div>
        </template>

        <template #item.select="{ item }">
          <div :data-testid="`included-concepts-row-checkbox-${(item as Concept).conceptId}`">
            <v-checkbox-btn
              :model-value="selectedSet.has((item as Concept).conceptId)"
              :aria-label="(item as Concept).conceptName"
              density="compact"
              hide-details
              @update:model-value="(v: boolean | null) => onToggleRow((item as Concept).conceptId, v)"
            />
          </div>
        </template>
        <template
          v-if="sourceKey"
          #item.conceptName="{ item }"
        >
          <a
            href="#"
            :data-testid="`included-name-link-${item.conceptId}`"
            class="concept-name-link"
            @click.prevent="onView(item)"
          >
            {{ item.conceptName }}
          </a>
        </template>

        <template
          v-else
          #item.conceptName="{ item }"
        >
          {{ item.conceptName }}
        </template>

        <template #item.domainId="{ item }">
          <AtlasChip
            v-if="item.domainId"
            :color="getDomainColor(item.domainId, themeStore.resolved)"
            size="xs"
            variant="tonal"
          >
            {{ item.domainId }}
          </AtlasChip>
        </template>

        <template #item.vocabularyId="{ item }">
          <AtlasChip
            v-if="item.vocabularyId"
            size="xs"
            variant="outlined"
          >
            {{ item.vocabularyId }}
          </AtlasChip>
        </template>

        <template #item.standardConcept="{ item }">
          <AtlasChip
            :color="getConceptTypeColor(item)"
            size="xs"
            variant="tonal"
          >
            {{ getConceptTypeLabel(item) }}
          </AtlasChip>
        </template>

        <template #item.invalidReason="{ item }">
          <AtlasChip
            v-if="item.invalidReason"
            tone="danger"
            size="xs"
            variant="tonal"
          >
            {{ t('commonErrors.invalid').value }}
          </AtlasChip>
        </template>

        <template
          v-for="key in COUNT_KEYS"
          :key="key"
          #[`item.${key}`]="{ item }"
        >
          <div class="d-flex align-center justify-end">
            <AtlasProgressCircular
              v-if="loadingRecordCounts && item[key] === undefined"
              indeterminate
              size="16"
              width="2"
              color="primary"
            />
            <span v-else>{{ formatCount(item[key]) }}</span>
          </div>
        </template>

        <template #no-data>
          <div class="included-concepts-table__no-match">
            <p class="included-concepts-table__no-match-text">
              {{ t('cs.manager.emptyFilteredMessage').value }}
            </p>
            <AtlasButton
              size="sm"
              variant="ghost"
              data-testid="included-clear-filters-btn"
              @click="clearFilters()"
            >
              {{ t('versions.filterClear').value }}
            </AtlasButton>
          </div>
        </template>

        <template #loading>
          <AtlasSkeleton
            v-for="i in 5"
            :key="i"
            type="table-row"
            class="mx-2"
          />
        </template>
      </AtlasDataTable>
    </AtlasCard>

    <div
      v-else
      class="included-concepts-table__empty"
    >
      <AtlasIcon
        icon="mdi-family-tree"
        size="36"
        class="included-concepts-table__empty-icon"
      />
      <p class="included-concepts-table__empty-text">
        {{ emptyMessage }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, toRef } from 'vue'
import { useI18n } from '@/composables/useI18n'
import type { Concept, ConceptAddFlags } from '@/models/concept-set.types'
import {
  AtlasAlert,
  AtlasButton,
  AtlasCard,
  AtlasChip,
  AtlasDataTable,
  AtlasIcon,
  AtlasProgressCircular,
  AtlasSelect,
  AtlasSkeleton,
} from '@/components/ui'
import ConceptFacetFilters from './ConceptFacetFilters.vue'
import ConceptAddOptions from './ConceptAddOptions.vue'
import { CONCEPT_FACETS, useConceptFacets } from '@/composables/useConceptFacets'
import { useConceptRecordCounts } from '@/composables/useConceptRecordCounts'
import { getDomainColor } from '@/utils/domain-colors'
import { useThemeStore } from '@/stores/theme'

const { t } = useI18n()
const themeStore = useThemeStore()

interface Props {
  items: Concept[]
  loading: boolean
  error: string | null
  manualCount: number
  sourceKey?: string
}
const props = defineProps<Props>()

const emit = defineEmits<{
  'view-concept': [payload: { conceptId: number; sourceKey: string }]
  'add-concepts': [concepts: Concept[], flags: Required<ConceptAddFlags>]
  retry: []
}>()

// Exclude rather than include: a concept already listed here is by definition
// in the set, so the reason to select it is almost always to take it back out.
const addFlags = ref<Required<ConceptAddFlags>>({
  isExcluded: true,
  includeDescendants: false,
  includeMapped: false,
})

const selected = ref<number[]>([])
const selectedSet = computed(() => new Set(selected.value))

// Select-all covers the rows the facets have left on screen, not every resolved
// concept: offering to select rows the user has filtered away would be a trap.
const visibleIds = computed(() => visibleConcepts.value.map(c => c.conceptId))
const allVisibleSelected = computed(
  () => visibleIds.value.length > 0 && visibleIds.value.every(id => selectedSet.value.has(id))
)
const someVisibleSelected = computed(() => visibleIds.value.some(id => selectedSet.value.has(id)))

function onToggleRow(conceptId: number, checked: boolean | null) {
  selected.value = checked
    ? [...selected.value, conceptId]
    : selected.value.filter(id => id !== conceptId)
}

function onToggleSelectAll(checked: boolean | null) {
  if (checked) {
    const merged = new Set([...selected.value, ...visibleIds.value])
    selected.value = [...merged]
    return
  }
  const dropped = new Set(visibleIds.value)
  selected.value = selected.value.filter(id => !dropped.has(id))
}

function onAddSelected() {
  if (selected.value.length === 0) return
  const picked = props.items.filter(c => selectedSet.value.has(c.conceptId))
  emit('add-concepts', picked, { ...addFlags.value })
  selected.value = []
}

const sortBy = ref([{ key: 'descendantRecordCount', order: 'desc' as const }])

const COUNT_KEYS = [
  'recordCount',
  'descendantRecordCount',
  'personCount',
  'descendantPersonCount',
] as const

function formatCount(count: number | undefined): string {
  return count === undefined ? '-' : count.toLocaleString()
}

const {
  conceptsWithCounts,
  loading: loadingRecordCounts,
  sources: recordCountSources,
  effectiveSourceKey: effectiveRecordCountSource,
  setSource: setRecordCountSource,
} = useConceptRecordCounts(toRef(props, 'items'))

const viewCountsForLabel = t('search.viewCountMessage')

/**
 * The footer's rows-per-page control only works when the caller round-trips the
 * value: binding a bare number makes v-data-table's model controlled, so the
 * user's choice is emitted and then overwritten by the unchanged prop. Owning it
 * here is what makes the dropdown live (issue #266).
 */
const pageSize = ref(50)

/**
 * Facets mirror this table's own categorical columns, so every filter the bar
 * offers corresponds to something on screen. Concept class is left out because
 * this table does not show it.
 */
const facets = CONCEPT_FACETS.filter(f => f.key !== 'conceptClassId')

const {
  facetOptions,
  selected: selectedFacets,
  textFilter,
  filteredConcepts: visibleConcepts,
  activeFilterCount,
  setFacet,
  setTextFilter,
  clearFilters,
} = useConceptFacets(conceptsWithCounts, facets)

const headers = [
  { title: '', key: 'select', sortable: false, width: '48px' },
  { title: t('columns.conceptId').value, key: 'conceptId', sortable: true, width: '90px' },
  { title: t('columns.conceptCode').value, key: 'conceptCode', sortable: true, width: '110px' },
  { title: t('columns.conceptName').value, key: 'conceptName', sortable: true },
  { title: t('columns.domain').value, key: 'domainId', sortable: true, width: '120px' },
  { title: t('columns.vocabulary').value, key: 'vocabularyId', sortable: true, width: '120px' },
  { title: t('columns.type').value, key: 'standardConcept', sortable: true, width: '120px' },
  { title: t('columns.validity').value, key: 'invalidReason', sortable: true, width: '90px' },
  { title: t('columns.rcTooltip').value, key: 'recordCount', sortable: true, width: '100px', align: 'end' as const },
  { title: t('columns.drcTooltip').value, key: 'descendantRecordCount', sortable: true, width: '100px', align: 'end' as const },
  { title: t('columns.pcTooltip').value, key: 'personCount', sortable: true, width: '100px', align: 'end' as const },
  { title: t('columns.dpcTooltip').value, key: 'descendantPersonCount', sortable: true, width: '100px', align: 'end' as const },
]

const emptyMessage = computed(() => {
  if (props.manualCount === 0) {
    return t(
      'cs.manager.includedEmptyNoManual',
    ).value
  }
  return t(
    'cs.manager.includedEmptyNoResolve',
  ).value
})

function getConceptTypeColor(concept: Concept): string {
  if (concept.standardConcept === 'S') return 'primary'
  if (concept.standardConcept === 'C') return 'info'
  return 'default'
}

function getConceptTypeLabel(concept: Concept): string {
  if (concept.standardConcept === 'S') return t('search.standard').value
  if (concept.standardConcept === 'C') return t('search.classification').value
  return t('search.nonStandard').value
}

function onView(c: Concept) {
  emit('view-concept', { conceptId: c.conceptId, sourceKey: props.sourceKey! })
}
</script>

<style scoped>
.included-concepts-table {
  width: 100%;
}

.included-concepts-table__filters {
  margin-bottom: 12px;
}

.included-concepts-table__no-match {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 24px;
}

.included-concepts-table__no-match-text {
  margin: 0;
  font-size: 14px;
  color: rgb(var(--v-theme-on-surface-variant));
}

.included-concepts-table__error {
  margin-bottom: 12px;
}

.included-concepts-table__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 56px 24px;
  border-radius: 12px;
  background: rgb(var(--v-theme-surface-variant));
}

.included-concepts-table__empty-icon {
  color: rgb(var(--v-theme-on-surface-variant));
  opacity: 0.7;
}

.included-concepts-table__empty-text {
  margin: 0;
  font-size: 14px;
  color: rgb(var(--v-theme-on-surface-variant));
  text-align: center;
  max-width: 480px;
}

.concept-name-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}

.concept-name-link:hover {
  text-decoration: underline;
}
</style>
